import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { TraceViewerDrawer } from "../components/qc/TraceViewerDrawer";
import { Drawer } from "../components/Drawer";
import { Icon } from "../components/ui/Icon";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, Chips } from "../components/mobile/primitives";
import { ScannerOverlay } from "../components/mobile/ScannerOverlay";
import { QcScanDrawer } from "../components/qc/QcScanDrawer";
import "./Items.css";

interface PendingLot {
  id: string; code: string; itemKind: "stock" | "semi" | "finished";
  itemCode: string; itemName: string; qtyOnHand: number; uom: string;
  producedAt: string; expiresAt: string | null;
}
interface QuarantineRow {
  id: string; lotId: string; lotCode: string; itemCode: string; itemName: string;
  qty: number; uom: string; lotQtyOnHand: number; lotStatus: string;
  reason: string; note: string | null; findings: string | null; images: string[]; status: string;
  locationCode: string | null; locationName: string | null; resolvedAt: string | null; createdAt: string;
}
interface ReleasedLot {
  id: string; code: string; itemKind: "stock" | "semi" | "finished";
  itemCode: string; itemName: string; qtyOnHand: number; uom: string;
  producedAt: string; expiresAt: string | null;
}

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
const tabBtn = (active: boolean): React.CSSProperties => ({
  padding: "8px 14px", fontSize: 13, fontWeight: 600, background: "transparent", border: 0,
  borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
  color: active ? "var(--primary)" : "var(--neutral-500)", cursor: "pointer", marginBottom: -1,
});

export function QcPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<"pending" | "quarantine" | "released">("pending");
  const [selectMode, setSelectMode] = useState(false); // mobil toplu-seç modu
  const pageSize = 20;

  // ── QC Bekleyen ──
  const [page, setPage] = useState(1);
  const [trace, setTrace] = useState<{ id: string; code: string } | null>(null);
  const [confirm, setConfirm] = useState<null | { title: string; message: ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);
  const [reject, setReject] = useState<PendingLot | null>(null);
  const [qcScanOpen, setQcScanOpen] = useState(false); // mobil QR tarayıcı (QC modu)
  const [scanLotId, setScanLotId] = useState<string | null>(null); // taranan lot → QC karar drawer'ı
  const pendingQ = trpc.qc.pendingLots.useQuery({ page, pageSize });
  const releaseMut = trpc.qc.releaseLot.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const rows = (pendingQ.data?.rows ?? []) as unknown as PendingLot[];
  const total = pendingQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Toplu seçim (QC Bekleyen) ──
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkReject, setBulkReject] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<{ tone: "success" | "warning"; text: string } | null>(null);
  useEffect(() => { setSelected(new Set()); }, [page]); // sayfa değişince seçim temizlenir
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  function toggleAll() {
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) rows.forEach((r) => n.delete(r.id));
      else rows.forEach((r) => n.add(r.id));
      return n;
    });
  }
  function toggleOne(id: string) {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  const releaseManyMut = trpc.qc.releaseLots.useMutation({
    onSuccess: (res) => {
      qc.invalidateQueries();
      setSelected(new Set());
      setBulkMsg({
        tone: res.failed.length ? "warning" : "success",
        text: `${res.released} lot serbest bırakıldı${res.readyOrders > 0 ? ` · ${res.readyOrders} sipariş sevkiyata hazır` : ""}${res.failed.length ? ` · ${res.failed.length} lot başarısız: ${res.failed.map((f) => `${f.code} (${f.error})`).join(", ")}` : ""}.`,
      });
    },
  });

  // ── Karantina ──
  const [qPage, setQPage] = useState(1);
  const [edit, setEdit] = useState<QuarantineRow | null>(null);
  const [qConfirm, setQConfirm] = useState<null | { title: string; message: ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);
  const quarantineQ = trpc.qc.quarantineList.useQuery({ status: "open", page: qPage, pageSize }, { enabled: tab === "quarantine" });
  const resolveMut = trpc.qc.quarantineResolve.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const qRows = (quarantineQ.data?.rows ?? []) as unknown as QuarantineRow[];
  const qTotal = quarantineQ.data?.total ?? 0;
  const qTotalPages = Math.max(1, Math.ceil(qTotal / pageSize));

  function handleRelease(l: PendingLot) {
    setConfirm({ title: "Lotu serbest bırak?", message: <><b>{l.code}</b> lotunu QC serbest bırak? Stok tüketilebilir/sevk edilebilir hale gelir.</>, tone: "primary", confirmLabel: "Serbest Bırak", run: () => releaseMut.mutate({ lotId: l.id }) });
  }
  function handleReject(l: PendingLot) { setReject(l); }

  // ═══ Aksiyon drawer/dialog'ları — masaüstü + mobil ORTAK (Drawer ≤767px'te tam-ekran) ═══
  const drawers = (
    <>
      <TraceViewerDrawer open={!!trace} lotId={trace?.id ?? null} lotCode={trace?.code} onClose={() => setTrace(null)} />
      <ConfirmDialog open={!!confirm} title={confirm?.title ?? ""} message={confirm?.message} tone={confirm?.tone ?? "primary"} confirmLabel={confirm?.confirmLabel ?? "Onayla"} cancelLabel="Vazgeç"
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }} onCancel={() => setConfirm(null)} />
      <ConfirmDialog open={!!qConfirm} title={qConfirm?.title ?? ""} message={qConfirm?.message} tone={qConfirm?.tone ?? "primary"} confirmLabel={qConfirm?.confirmLabel ?? "Onayla"} cancelLabel="Vazgeç"
        onConfirm={() => { const c = qConfirm; setQConfirm(null); c?.run(); }} onCancel={() => setQConfirm(null)} />
      <RejectLotDrawer lot={reject} onClose={() => setReject(null)} />
      <BulkRejectDrawer
        open={bulkReject}
        lotIds={[...selected]}
        onClose={() => setBulkReject(false)}
        onDone={(res) => {
          setBulkReject(false);
          setSelected(new Set());
          setBulkMsg({
            tone: res.failed.length ? "warning" : "success",
            text: `${res.rejected} lot reddedilip karantinaya alındı${res.failed.length ? ` · ${res.failed.length} lot başarısız: ${res.failed.map((f) => `${f.code} (${f.error})`).join(", ")}` : ""}.`,
          });
        }}
      />
      <QuarantineEditDrawer rec={edit} onClose={() => setEdit(null)} />
    </>
  );

  const errBanner = (releaseMut.error || resolveMut.error || releaseManyMut.error);
  const bulkBannerNode = bulkMsg ? (
    <div role="status" style={{
      display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 12px",
      borderRadius: "var(--radius-md)", fontSize: 12.5, lineHeight: 1.45,
      border: `1px solid ${bulkMsg.tone === "success" ? "var(--success)" : "var(--warning)"}`,
      background: "var(--neutral-50)", color: bulkMsg.tone === "success" ? "var(--success)" : "var(--warning)",
    }}>
      <Icon name={bulkMsg.tone === "success" ? "checkCircle" : "alertTriangle"} size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span style={{ color: "var(--neutral-700)" }}>{bulkMsg.text}</span>
      <span style={{ flex: 1 }} />
      <button type="button" aria-label="Bildirimi kapat" onClick={() => setBulkMsg(null)} style={{ border: 0, background: "transparent", color: "var(--neutral-400)", cursor: "pointer", padding: 0, lineHeight: 1 }}><Icon name="x" size={14} /></button>
    </div>
  ) : null;

  // ═══ MOBİL GÖRÜNÜM (≤767px) — chips + kart listeleri + toplu-seç ═══
  if (isMobile) {
    const pager = (pg: number, tp: number, set: (u: (p: number) => number) => void) =>
      tp > 1 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
          <button type="button" className="btn-md secondary sm" disabled={pg === 1} onClick={() => set((n) => Math.max(1, n - 1))}>‹ Önceki</button>
          <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{pg} / {tp}</span>
          <button type="button" className="btn-md secondary sm" disabled={pg >= tp} onClick={() => set((n) => Math.min(tp, n + 1))}>Sonraki ›</button>
        </div>
      ) : null;
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={() => setQcScanOpen(true)}>
            <Icon name="qr" size={17} />QR ile Tara — Onayla / Reddet
          </button>
          <Chips>
            <button type="button" className={`m-chip ${tab === "pending" ? "act" : ""}`} onClick={() => setTab("pending")}>QC Bekleyen{total > 0 ? ` (${total})` : ""}</button>
            <button type="button" className={`m-chip ${tab === "released" ? "act" : ""}`} onClick={() => setTab("released")}>Serbest</button>
            <button type="button" className={`m-chip ${tab === "quarantine" ? "act" : ""}`} onClick={() => setTab("quarantine")}>Karantina{qTotal > 0 ? ` (${qTotal})` : ""}</button>
          </Chips>
        </div>
        {errBanner ? <div className="form-banner-err">{errBanner.message}</div> : null}
        {bulkBannerNode}

        {tab === "pending" ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="m-ovl"><b className="mono">{total}</b> lot QC bekliyor</span>
              <span style={{ flex: 1 }} />
              <button type="button" className={`btn-md sm ${selectMode ? "primary" : "secondary"}`} onClick={() => { setSelectMode((s) => !s); setSelected(new Set()); }}>
                <Icon name="checkCircle" size={14} />{selectMode ? "Bitir" : "Seç"}
              </button>
            </div>
            {pendingQ.isLoading ? (
              <div className="m-empty">Yükleniyor…</div>
            ) : rows.length === 0 ? (
              <div className="m-empty">QC kuyruğu boş — bekleyen lot yok</div>
            ) : (
              rows.map((l) => {
                const sel = selected.has(l.id);
                return (
                  <MCard pad key={l.id} className={sel ? "sel" : ""}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      {selectMode ? (
                        <button type="button" className="m-cardcheck" aria-label={`${l.code} seç`} aria-pressed={sel} onClick={() => toggleOne(l.id)}>{sel ? <Icon name="check" size={14} /> : null}</button>
                      ) : null}
                      <span style={{ flex: 1, minWidth: 0 }} onClick={selectMode ? () => toggleOne(l.id) : undefined}>
                        <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{l.itemName}</span>
                        <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{l.itemCode}</span>
                      </span>
                      <span className="badge b-warning no-dot">QC Bekliyor</span>
                    </div>
                    <div className="mono" style={{ fontSize: 11.5, fontWeight: 600, color: "var(--neutral-600)", marginTop: 6 }}>{l.code}</div>
                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                      {([["Miktar", `${fmtQty(l.qtyOnHand)} ${l.uom}`], ["Üretim", fmtDate(l.producedAt)], ["SKT", fmtDate(l.expiresAt)]] as Array<[string, string]>).map(([lb, v]) => (
                        <div key={lb} style={{ flex: 1 }}>
                          <div className="m-ovl">{lb}</div>
                          <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {!selectMode ? (
                      <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                        <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Serbest Bırak" onClick={() => handleRelease(l)} disabled={releaseMut.isPending}><Icon name="check" size={16} /></button>
                        <button type="button" className="m-iconbtn m-li-act danger" aria-label="Reddet → karantina" onClick={() => handleReject(l)}><Icon name="x" size={16} /></button>
                        <button type="button" className="m-iconbtn m-li-act" aria-label="İzle" onClick={() => setTrace({ id: l.id, code: l.code })}><Icon name="search" size={16} /></button>
                      </div>
                    ) : null}
                  </MCard>
                );
              })
            )}
            {pager(page, totalPages, setPage)}
            {selectMode && selected.size > 0 ? (
              <div className="m-selbar">
                <span className="cnt">{selected.size} seçili</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="btn-md secondary sm" onClick={() => setSelected(new Set())}>Temizle</button>
                <button type="button" className="btn-md danger sm" disabled={releaseManyMut.isPending} onClick={() => setBulkReject(true)}><Icon name="x" size={14} />Reddet ({selected.size})</button>
                <button type="button" className="btn-md primary sm" disabled={releaseManyMut.isPending}
                  onClick={() => setConfirm({
                    title: "Seçilen lotları serbest bırak?",
                    message: <><b className="mono">{selected.size}</b> lot QC serbest bırakılacak — stok tüketilebilir/sevk edilebilir hale gelir.</>,
                    tone: "primary", confirmLabel: `Serbest Bırak (${selected.size})`,
                    run: () => releaseManyMut.mutate({ lotIds: [...selected] }),
                  })}>
                  <Icon name="check" size={14} />Serbest ({selected.size})
                </button>
              </div>
            ) : null}
          </>
        ) : tab === "quarantine" ? (
          <>
            <span className="m-ovl"><b className="mono">{qTotal}</b> karantina kaydı (açık)</span>
            {quarantineQ.isLoading ? (
              <div className="m-empty">Yükleniyor…</div>
            ) : qRows.length === 0 ? (
              <div className="m-empty">Karantina boş</div>
            ) : (
              qRows.map((r) => (
                <MCard pad key={r.id}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{r.itemName}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.lotCode}</span>
                    </span>
                    <span className="badge b-danger no-dot">Karantina</span>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1 }}><div className="m-ovl">Miktar</div><div className="mono" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>{fmtQty(r.qty)} {r.uom}</div></div>
                    <div style={{ flex: 1.4 }}><div className="m-ovl">Karantina Alanı</div><div style={{ fontSize: 12.5, fontWeight: 500, marginTop: 2, color: r.locationName ? "var(--neutral-800)" : "var(--neutral-400)" }}>{r.locationName ?? "—"}</div></div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div className="m-ovl">Sebep</div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--danger-text)", marginTop: 2 }}>{r.reason}</div>
                    {r.findings ? <div style={{ fontSize: 12, color: "var(--neutral-600)", marginTop: 3 }}>Tespit: {r.findings}</div> : null}
                    {r.note ? <div style={{ fontSize: 12, color: "var(--neutral-500)", marginTop: 1 }}>Not: {r.note}</div> : null}
                    {r.images.length ? <span className="badge b-info no-dot" style={{ marginTop: 6 }}>{r.images.length} foto</span> : null}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                    <button type="button" className="m-iconbtn m-li-act" aria-label="Not / Tespit" onClick={() => setEdit(r)}><Icon name="edit" size={16} /></button>
                    <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Serbest Bırak" disabled={resolveMut.isPending}
                      onClick={() => setQConfirm({ title: "Karantinadan serbest bırak?", message: <><b>{r.lotCode}</b> incelendi ve uygun bulundu → lot tekrar serbest (kullanılabilir) olur.</>, tone: "primary", confirmLabel: "Serbest Bırak", run: () => resolveMut.mutate({ id: r.id, action: "release" }) })}>
                      <Icon name="check" size={16} />
                    </button>
                    <button type="button" className="m-iconbtn m-li-act danger" aria-label="Hurda" disabled={resolveMut.isPending}
                      onClick={() => setQConfirm({ title: "Karantina ürününü hurda et?", message: <><b>{r.lotCode}</b> ({fmtQty(r.qty)} {r.uom}) <b>hurdaya</b> ayrılacak; stok düşülür. Geri alınamaz.</>, tone: "danger", confirmLabel: "Hurda Et", run: () => resolveMut.mutate({ id: r.id, action: "scrap" }) })}>
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                </MCard>
              ))
            )}
            {pager(qPage, qTotalPages, setQPage)}
          </>
        ) : (
          <ReleasedLotsTab onTrace={(t) => setTrace(t)} isMobile />
        )}

        {drawers}
        {qcScanOpen ? (
          <ScannerOverlay title="QC — QR Tara" onClose={() => setQcScanOpen(false)} onLot={(lot) => setScanLotId(lot.lotId)} />
        ) : null}
        <QcScanDrawer lotId={scanLotId} onClose={() => setScanLotId(null)} onDone={() => setScanLotId(null)} />
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Kalite Kontrol</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Kalite Kontrol</h1>
          <p className="sub">QC bekleyen lotlar · karantina yönetimi · izlenebilirlik</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--neutral-200)", marginBottom: 16 }}>
        <button type="button" style={tabBtn(tab === "pending")} onClick={() => setTab("pending")}>QC Bekleyen{total > 0 ? ` (${total})` : ""}</button>
        <button type="button" style={tabBtn(tab === "released")} onClick={() => setTab("released")}>Serbest Lotlar</button>
        <button type="button" style={tabBtn(tab === "quarantine")} onClick={() => setTab("quarantine")}>Karantina{qTotal > 0 ? ` (${qTotal})` : ""}</button>
      </div>

      {(releaseMut.error || resolveMut.error || releaseManyMut.error) ? (
        <div className="form-banner-err" style={{ marginBottom: 12 }}>{(releaseMut.error || resolveMut.error || releaseManyMut.error)?.message}</div>
      ) : null}
      {bulkMsg ? (
        <div role="status" style={{
          display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 12px", marginBottom: 12,
          borderRadius: "var(--radius-md)", fontSize: 12.5, lineHeight: 1.45,
          border: `1px solid ${bulkMsg.tone === "success" ? "var(--success)" : "var(--warning)"}`,
          background: "var(--neutral-50)",
          color: bulkMsg.tone === "success" ? "var(--success)" : "var(--warning)",
        }}>
          <Icon name={bulkMsg.tone === "success" ? "checkCircle" : "alertTriangle"} size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ color: "var(--neutral-700)" }}>{bulkMsg.text}</span>
          <span style={{ flex: 1 }} />
          <button type="button" aria-label="Bildirimi kapat" onClick={() => setBulkMsg(null)} style={{ border: 0, background: "transparent", color: "var(--neutral-400)", cursor: "pointer", padding: 0, lineHeight: 1 }}><Icon name="x" size={14} /></button>
        </div>
      ) : null}

      {tab === "pending" ? (
        <div className="dt2-wrap">
          <div className="dt2-toolbar">
            <span style={{ fontSize: 13, color: "var(--neutral-600)" }}><b className="mono">{total}</b> lot QC bekliyor</span>
            {selected.size > 0 ? (
              <>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--primary)" }}><b className="mono">{selected.size}</b> seçili</span>
                <button
                  type="button" className="btn-md primary" style={{ height: 30, fontSize: 12.5 }}
                  disabled={releaseManyMut.isPending}
                  onClick={() => setConfirm({
                    title: "Seçilen lotları serbest bırak?",
                    message: <><b className="mono">{selected.size}</b> lot QC serbest bırakılacak — stok tüketilebilir/sevk edilebilir hale gelir. Bekleyen sipariş tamamlanıyorsa "sevkiyata hazır" olur.</>,
                    tone: "primary", confirmLabel: `Serbest Bırak (${selected.size})`,
                    run: () => releaseManyMut.mutate({ lotIds: [...selected] }),
                  })}
                >
                  <Icon name="check" size={14} />{releaseManyMut.isPending ? "İşleniyor…" : `Serbest Bırak (${selected.size})`}
                </button>
                <button type="button" className="btn-md danger" style={{ height: 30, fontSize: 12.5 }} onClick={() => setBulkReject(true)} disabled={releaseManyMut.isPending}>
                  <Icon name="x" size={14} />Reddet ({selected.size})
                </button>
                <button type="button" className="btn" onClick={() => setSelected(new Set())}>Temizle</button>
              </>
            ) : null}
            <span className="grow" />
            <button type="button" className="btn" onClick={() => pendingQ.refetch()} disabled={pendingQ.isFetching}>{pendingQ.isFetching ? "Yükleniyor…" : "Yenile"}</button>
          </div>
          <div className="dt2-scroll">
            {pendingQ.isLoading ? (
              <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
            ) : rows.length === 0 ? (
              <div className="empty-state"><p className="ttl">QC kuyruğu boş</p><p className="sub">Bekleyen lot yok. Üretilen mamuller (qcRequired) burada görünür.</p></div>
            ) : (
              <table className="dt2">
                <thead><tr>
                  <th style={{ width: 34 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Sayfadaki tüm lotları seç" title="Tümünü seç (bu sayfa)" style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: "pointer", display: "block" }} />
                  </th>
                  <th style={{ width: 150 }}>Lot</th><th>Ürün</th><th className="num" style={{ width: 110 }}>Miktar</th>
                  <th style={{ width: 110 }}>Üretim</th><th style={{ width: 110 }}>SKT</th><th style={{ width: 110 }}>Eylemler</th>
                </tr></thead>
                <tbody>
                  {rows.map((l) => (
                    <tr key={l.id} style={selected.has(l.id) ? { background: "var(--primary-subtle)" } : undefined}>
                      <td>
                        <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleOne(l.id)} aria-label={`${l.code} lotunu seç`} style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: "pointer", display: "block" }} />
                      </td>
                      <td><span className="cell-id">{l.code}</span></td>
                      <td>{l.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{l.itemCode}</span></td>
                      <td className="cell-num">{fmtQty(l.qtyOnHand)}<span className="unit">{l.uom}</span></td>
                      <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(l.producedAt)}</span></td>
                      <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(l.expiresAt)}</span></td>
                      <td><div className="row-acts">
                        <button type="button" className="iact success" aria-label="Serbest Bırak" title="Serbest Bırak" onClick={() => handleRelease(l)} disabled={releaseMut.isPending}><Icon name="check" size={15} /></button>
                        <button type="button" className="iact danger" aria-label="Reddet (karantina)" title="Reddet → karantina" onClick={() => handleReject(l)}><Icon name="x" size={15} /></button>
                        <button type="button" className="iact info" aria-label="İzle" title="İzle" onClick={() => setTrace({ id: l.id, code: l.code })}><Icon name="search" size={15} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="dt2-foot">
            <div><b>{total}</b> lot · <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası</div>
            <div className="pager">
              <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
              <span className="pg active">{page}</span>
              <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
              <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
            </div>
          </div>
        </div>
      ) : tab === "quarantine" ? (
        <div className="dt2-wrap">
          <div className="dt2-toolbar">
            <span style={{ fontSize: 13, color: "var(--neutral-600)" }}><b className="mono">{qTotal}</b> karantina kaydı (açık)</span>
            <span className="grow" />
            <button type="button" className="btn" onClick={() => quarantineQ.refetch()} disabled={quarantineQ.isFetching}>{quarantineQ.isFetching ? "Yükleniyor…" : "Yenile"}</button>
          </div>
          <div className="dt2-scroll">
            {quarantineQ.isLoading ? (
              <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
            ) : qRows.length === 0 ? (
              <div className="empty-state"><p className="ttl">Karantina boş</p><p className="sub">QC reddedilen lotlar burada tutulur; not/tespit ekleyip serbest bırakabilir ya da hurda edebilirsiniz.</p></div>
            ) : (
              <table className="dt2">
                <thead><tr>
                  <th style={{ width: 150 }}>Lot</th><th>Ürün</th><th className="num" style={{ width: 100 }}>Miktar</th>
                  <th style={{ width: 130 }}>Karantina Alanı</th><th>Sebep / Tespit</th><th style={{ width: 150 }}>Eylemler</th>
                </tr></thead>
                <tbody>
                  {qRows.map((r) => (
                    <tr key={r.id}>
                      <td><span className="cell-id">{r.lotCode}</span></td>
                      <td>{r.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{r.itemCode}</span></td>
                      <td className="cell-num">{fmtQty(r.qty)}<span className="unit">{r.uom}</span></td>
                      <td>{r.locationName ? <>{r.locationName}<span className="sec mono" style={{ fontSize: 10.5 }}>{r.locationCode}</span></> : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                      <td style={{ fontSize: 12 }}>
                        <div><b style={{ color: "var(--danger-text)" }}>{r.reason}</b></div>
                        {r.findings ? <div style={{ color: "var(--neutral-600)", marginTop: 2 }}>Tespit: {r.findings}</div> : null}
                        {r.note ? <div style={{ color: "var(--neutral-500)", marginTop: 1 }}>Not: {r.note}</div> : null}
                        {r.images.length ? <div style={{ color: "var(--info-text)", marginTop: 1, fontSize: 11 }}>{r.images.length} foto</div> : null}
                      </td>
                      <td><div className="row-acts">
                        <button type="button" className="iact info" aria-label="Not / Tespit" title="Not / Tespit ekle" onClick={() => setEdit(r)}><Icon name="edit" size={14} /></button>
                        <button type="button" className="iact success" aria-label="Serbest Bırak" title="Serbest bırak (lot tekrar kullanılabilir)" disabled={resolveMut.isPending}
                          onClick={() => setQConfirm({ title: "Karantinadan serbest bırak?", message: <><b>{r.lotCode}</b> incelendi ve uygun bulundu → lot tekrar serbest (kullanılabilir) olur.</>, tone: "primary", confirmLabel: "Serbest Bırak", run: () => resolveMut.mutate({ id: r.id, action: "release" }) })}>
                          <Icon name="check" size={15} />
                        </button>
                        <button type="button" className="iact danger" aria-label="Hurda" title="Hurda et (stok düşülür)" disabled={resolveMut.isPending}
                          onClick={() => setQConfirm({ title: "Karantina ürününü hurda et?", message: <><b>{r.lotCode}</b> ({fmtQty(r.qty)} {r.uom}) <b>hurdaya</b> ayrılacak; stok düşülür. Geri alınamaz.</>, tone: "danger", confirmLabel: "Hurda Et", run: () => resolveMut.mutate({ id: r.id, action: "scrap" }) })}>
                          <Icon name="trash" size={15} />
                        </button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="dt2-foot">
            <div><b>{qTotal}</b> kayıt · <b>{qRows.length === 0 ? 0 : (qPage - 1) * pageSize + 1}–{(qPage - 1) * pageSize + qRows.length}</b> arası</div>
            <div className="pager">
              <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setQPage((p) => Math.max(1, p - 1))} disabled={qPage === 1}>‹</button>
              <span className="pg active">{qPage}</span>
              <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {qTotalPages}</span>
              <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setQPage((p) => Math.min(qTotalPages, p + 1))} disabled={qPage >= qTotalPages}>›</button>
            </div>
          </div>
        </div>
      ) : (
        <ReleasedLotsTab onTrace={(t) => setTrace(t)} />
      )}

      {drawers}
    </div>
  );
}

/** Serbest (QC onaylı) lotlar sekmesi: listele + sonradan uygunsuzluk bildir → karantinaya al. */
function ReleasedLotsTab({ onTrace, isMobile = false }: { onTrace: (t: { id: string; code: string }) => void; isMobile?: boolean }) {
  const pageSize = 20;
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [flag, setFlag] = useState<ReleasedLot | null>(null);
  const listQ = trpc.qc.releasedLots.useQuery({ q: q.trim() || undefined, page, pageSize });
  const rows = (listQ.data?.rows ?? []) as unknown as ReleasedLot[];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <div className="m-search" style={{ height: 44 }}>
            <Icon name="search" size={15} />
            <input type="search" placeholder="Lot kodu, ürün ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
        </div>
        <span className="m-ovl"><b className="mono">{total}</b> serbest lot</span>
        {listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">Serbest lot yok</div>
        ) : (
          rows.map((l) => (
            <MCard pad key={l.id}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{l.itemName}</span>
                  <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{l.itemCode}</span>
                </span>
                <span className="badge b-success no-dot">Serbest</span>
              </div>
              <div className="mono" style={{ fontSize: 11.5, fontWeight: 600, color: "var(--neutral-600)", marginTop: 6 }}>{l.code}</div>
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                {([["Eldeki", `${fmtQty(l.qtyOnHand)} ${l.uom}`], ["Üretim", fmtDate(l.producedAt)], ["SKT", fmtDate(l.expiresAt)]] as Array<[string, string]>).map(([lb, v]) => (
                  <div key={lb} style={{ flex: 1 }}>
                    <div className="m-ovl">{lb}</div>
                    <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                <button type="button" className="m-iconbtn m-li-act danger" aria-label="Uygunsuzluk bildir → karantina" onClick={() => setFlag(l)}><Icon name="alertTriangle" size={16} /></button>
                <button type="button" className="m-iconbtn m-li-act" aria-label="İzle" onClick={() => onTrace({ id: l.id, code: l.code })}><Icon name="search" size={16} /></button>
              </div>
            </MCard>
          ))
        )}
        {totalPages > 1 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
            <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
            <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
            <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
          </div>
        ) : null}
        <FlagReleasedLotDrawer lot={flag} onClose={() => setFlag(null)} />
      </>
    );
  }

  return (
    <>
      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Lot kodu, ürün ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <span style={{ fontSize: 13, color: "var(--neutral-600)" }}><b className="mono">{total}</b> serbest lot</span>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => listQ.refetch()} disabled={listQ.isFetching}>{listQ.isFetching ? "Yükleniyor…" : "Yenile"}</button>
        </div>
        <div className="dt2-scroll">
          {listQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state"><p className="ttl">Serbest lot yok</p><p className="sub">QC onaylı (released) lotlar burada listelenir; sonradan uygunsuzluk bildirip karantinaya alabilirsiniz.</p></div>
          ) : (
            <table className="dt2">
              <thead><tr>
                <th style={{ width: 150 }}>Lot</th><th>Ürün</th><th className="num" style={{ width: 110 }}>Eldeki</th>
                <th style={{ width: 110 }}>Üretim</th><th style={{ width: 110 }}>SKT</th><th style={{ width: 150 }}>Eylemler</th>
              </tr></thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id}>
                    <td><span className="cell-id">{l.code}</span></td>
                    <td>{l.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{l.itemCode}</span></td>
                    <td className="cell-num">{fmtQty(l.qtyOnHand)}<span className="unit">{l.uom}</span></td>
                    <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(l.producedAt)}</span></td>
                    <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(l.expiresAt)}</span></td>
                    <td><div className="row-acts">
                      <button type="button" className="iact danger" aria-label="Uygunsuzluk bildir (karantina)" title="Uygunsuzluk bildir → karantina" onClick={() => setFlag(l)}><Icon name="alertTriangle" size={15} /></button>
                      <button type="button" className="iact info" aria-label="İzle" title="İzle" onClick={() => onTrace({ id: l.id, code: l.code })}><Icon name="search" size={15} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="dt2-foot">
          <div><b>{total}</b> lot · <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      <FlagReleasedLotDrawer lot={flag} onClose={() => setFlag(null)} />
    </>
  );
}

/** QC bekleyen lotu reddet → karantina drawer'ı (sebep zorunlu). */
function RejectLotDrawer({ lot, onClose }: { lot: PendingLot | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const mut = trpc.qc.rejectLot.useMutation({ onSuccess: () => { qc.invalidateQueries(); onClose(); } });
  useEffect(() => { if (lot) { setReason(""); mut.reset(); } /* eslint-disable-next-line */ }, [lot?.id]);
  const canSubmit = reason.trim().length > 0 && !mut.isPending;
  return (
    <Drawer open={!!lot} onClose={onClose} size="sm" title="Lotu reddet → karantina" subtitle={lot ? `${lot.code} · ${lot.itemName}` : undefined}
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose} disabled={mut.isPending}>Vazgeç</button>
        <button type="button" className="btn-md danger" disabled={!canSubmit}
          onClick={() => lot && mut.mutate({ lotId: lot.id, reason: reason.trim() })}>
          {mut.isPending ? "İşleniyor…" : "Reddet → Karantina"}
        </button>
      </>}>
      {mut.error ? <div className="form-banner-err">{mut.error.message}</div> : null}
      <div className="form-field">
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-700)", lineHeight: 1.5 }}>
          <Icon name="alertTriangle" size={16} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
          <span><b className="mono">{lot?.code}</b> lotu QC reddedilip <b>karantinaya</b> alınacak (hurda edilmez). Eksik kalan üretim aynı <b>iş emrine (yarı mamuller dâhil)</b> eklenir.</span>
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="reject-reason">Red sebebi <span style={{ color: "var(--danger)" }}>*</span></label>
        <textarea id="reject-reason" className="form-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={4} autoFocus
          placeholder="Örn. mikrobiyolojik uygunsuzluk, ambalaj hasarı…" />
        <span className="help">Bu sebep karantina kaydına işlenir; sonra Karantina sekmesinden tespit/foto ekleyebilirsiniz.</span>
      </div>
    </Drawer>
  );
}

/** TOPLU red → karantina drawer'ı: tek sebep, seçili tüm lotlara uygulanır. */
function BulkRejectDrawer({ open, lotIds, onClose, onDone }: {
  open: boolean;
  lotIds: string[];
  onClose: () => void;
  onDone: (res: { rejected: number; failed: Array<{ code: string; error: string }> }) => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const mut = trpc.qc.rejectLots.useMutation({ onSuccess: (res) => { qc.invalidateQueries(); onDone(res); } });
  useEffect(() => { if (open) { setReason(""); mut.reset(); } /* eslint-disable-next-line */ }, [open]);
  const canSubmit = reason.trim().length > 0 && lotIds.length > 0 && !mut.isPending;
  return (
    <Drawer open={open} onClose={onClose} size="sm" title="Seçili lotları reddet → karantina" subtitle={`${lotIds.length} lot seçili`}
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose} disabled={mut.isPending}>Vazgeç</button>
        <button type="button" className="btn-md danger" disabled={!canSubmit}
          onClick={() => mut.mutate({ lotIds, reason: reason.trim() })}>
          {mut.isPending ? "İşleniyor…" : `Reddet → Karantina (${lotIds.length})`}
        </button>
      </>}>
      {mut.error ? <div className="form-banner-err">{mut.error.message}</div> : null}
      <div className="form-field">
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-700)", lineHeight: 1.5 }}>
          <Icon name="alertTriangle" size={16} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
          <span>Seçili <b className="mono">{lotIds.length}</b> lotun tamamı QC reddedilip <b>karantinaya</b> alınacak (hurda edilmez). Eksik kalan üretimler ilgili <b>iş emirlerine (yarı mamuller dâhil)</b> eklenir. Aşağıdaki sebep hepsine işlenir.</span>
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="bulk-reject-reason">Red sebebi (tümü için) <span style={{ color: "var(--danger)" }}>*</span></label>
        <textarea id="bulk-reject-reason" className="form-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={4} autoFocus
          placeholder="Örn. mikrobiyolojik uygunsuzluk, ambalaj hasarı…" />
        <span className="help">Bu sebep seçili tüm lotların karantina kaydına işlenir; sonra Karantina sekmesinden lot bazında tespit/foto ekleyebilirsiniz.</span>
      </div>
    </Drawer>
  );
}

/** Serbest (onaylı) lota uygunsuzluk bildir → karantina drawer'ı (sebep zorunlu). */
function FlagReleasedLotDrawer({ lot, onClose }: { lot: ReleasedLot | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const mut = trpc.qc.flagReleasedLot.useMutation({ onSuccess: () => { qc.invalidateQueries(); onClose(); } });
  useEffect(() => { if (lot) { setReason(""); mut.reset(); } /* eslint-disable-next-line */ }, [lot?.id]);
  const canSubmit = reason.trim().length > 0 && !mut.isPending;
  return (
    <Drawer open={!!lot} onClose={onClose} size="sm" title="Uygunsuzluk bildir → karantina" subtitle={lot ? `${lot.code} · ${lot.itemName}` : undefined}
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose} disabled={mut.isPending}>Vazgeç</button>
        <button type="button" className="btn-md danger" disabled={!canSubmit}
          onClick={() => lot && mut.mutate({ lotId: lot.id, reason: reason.trim() })}>
          {mut.isPending ? "İşleniyor…" : "Karantinaya Al"}
        </button>
      </>}>
      {mut.error ? <div className="form-banner-err">{mut.error.message}</div> : null}
      <div className="form-field">
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-700)", lineHeight: 1.5 }}>
          <Icon name="alertTriangle" size={16} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
          <span><b className="mono">{lot?.code}</b> serbest (onaylı) lotuna uygunsuzluk verilecek: lot <b>karantinaya</b> alınır (hurda edilmez), eldeki miktar Üretilen&apos;den düşer ve ilgili <b>sipariş/iş emri (yarı mamuller dâhil)</b> ikmal için tekrar üretime döner.</span>
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="flag-reason">Uygunsuzluk sebebi <span style={{ color: "var(--danger)" }}>*</span></label>
        <textarea id="flag-reason" className="form-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={4} autoFocus
          placeholder="Örn. raf ömründe bozulma, müşteri şikâyeti, mikrobiyolojik bulgu…" />
        <span className="help">Bu sebep karantina kaydına işlenir; sonra Karantina sekmesinden tespit/foto ekleyebilirsiniz.</span>
      </div>
    </Drawer>
  );
}

/** Karantina kaydına not + tespit ekleme/düzenleme drawer'ı (resim yükleme Deploy 3). */
function QuarantineEditDrawer({ rec, onClose }: { rec: QuarantineRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [findings, setFindings] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const mut = trpc.qc.quarantineUpdate.useMutation();
  const uploadMut = trpc.qc.quarantineUploadImage.useMutation();
  const removeMut = trpc.qc.quarantineRemoveImage.useMutation();
  useEffect(() => { if (rec) { setNote(rec.note ?? ""); setFindings(rec.findings ?? ""); setImages(rec.images ?? []); mut.reset(); uploadMut.reset(); } /* eslint-disable-next-line */ }, [rec?.id]);

  function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !rec) return;
    const reader = new FileReader();
    reader.onload = () => uploadMut.mutate(
      { id: rec.id, filename: file.name, dataBase64: reader.result as string },
      { onSuccess: (res) => { setImages((p) => [...p, res.url]); qc.invalidateQueries(); } },
    );
    reader.readAsDataURL(file);
  }
  function removeImage(url: string) {
    if (!rec) return;
    removeMut.mutate({ id: rec.id, url }, { onSuccess: () => { setImages((p) => p.filter((u) => u !== url)); qc.invalidateQueries(); } });
  }
  return (
    <Drawer open={!!rec} onClose={onClose} size="sm" title="Karantina — Not / Tespit" subtitle={rec ? `${rec.lotCode} · ${rec.itemName}` : undefined}
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose} disabled={mut.isPending}>Kapat</button>
        <button type="button" className="btn-md primary" disabled={mut.isPending}
          onClick={() => rec && mut.mutate({ id: rec.id, note: note.trim() || null, findings: findings.trim() || null }, { onSuccess: () => { qc.invalidateQueries(); onClose(); } })}>
          {mut.isPending ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </>}>
      {mut.error ? <div className="form-banner-err">{mut.error.message}</div> : null}
      <div className="form-field">
        <label className="lbl">Red Sebebi</label>
        <div style={{ padding: "8px 10px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 13, color: "var(--danger-text)", fontWeight: 600 }}>{rec?.reason}</div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="q-findings">Tespit</label>
        <textarea id="q-findings" className="form-textarea" value={findings} onChange={(e) => setFindings(e.target.value)} maxLength={2000} rows={3} placeholder="İnceleme bulgusu / kök neden…" />
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="q-note">Not</label>
        <textarea id="q-note" className="form-textarea" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} placeholder="Ek not…" />
      </div>
      <div className="form-field">
        <label className="lbl">Fotoğraflar</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {images.map((url) => (
            <div key={url} style={{ position: "relative", width: 74, height: 74, borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--neutral-200)" }}>
              <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="Karantina fotoğrafı" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /></a>
              <button type="button" aria-label="Fotoğrafı kaldır" onClick={() => removeImage(url)} disabled={removeMut.isPending}
                style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: "50%", border: 0, background: "rgba(15,23,42,0.65)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          ))}
          <label style={{ width: 74, height: 74, borderRadius: "var(--radius-md)", border: "1px dashed var(--neutral-300)", display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: uploadMut.isPending ? "wait" : "pointer", color: "var(--neutral-500)", fontSize: 11, gap: 3 }}>
            <Icon name="upload" size={16} />{uploadMut.isPending ? "…" : "Ekle"}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={onPickImage} disabled={uploadMut.isPending} />
          </label>
        </div>
        <span className="help">PNG / JPG / WEBP / GIF · en fazla 5 MB.</span>
        {uploadMut.error ? <span className="help" style={{ color: "var(--danger)" }}>{uploadMut.error.message}</span> : null}
      </div>
    </Drawer>
  );
}
